import { useMemo, useState } from 'react';
import { groupTabs, topicList } from '@/mocks/homeData';

export default function TopicWall() {
  const [activeGroup, setActiveGroup] = useState('all');
  const [selected, setSelected] = useState('t1');

  const visibleTopics = useMemo(
    () => (activeGroup === 'all' ? topicList : topicList.filter((topic) => topic.group === activeGroup)),
    [activeGroup],
  );

  const hotRanking = useMemo(
    () => [...topicList].sort((a, b) => b.heat - a.heat).slice(0, 5),
    [],
  );

  const selectedTopic = topicList.find((topic) => topic.id === selected) ?? topicList[0];

  return (
    <section id="topics" className="w-full bg-background-50 py-14 md:py-20">
      <div className="mx-auto w-full max-w-7xl px-4 md:px-8">
        <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div>
            <span className="font-label text-xs tracking-[0.3em] text-primary-600">TOPIC WALL</span>
            <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              热搜话题墙
            </h2>
            <p className="mt-2 max-w-xl text-sm text-foreground-600">
              把零散的讨论归到一句话话题下，点一下就能看到这块目前的讨论方向。
            </p>
          </div>
          <div className="flex flex-wrap gap-1 rounded-full border border-background-200 bg-background-100 p-1">
            {groupTabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveGroup(tab.id)}
                className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs transition-colors ${
                  activeGroup === tab.id
                    ? 'bg-primary-500 font-medium text-background-50'
                    : 'text-foreground-600 hover:text-primary-600'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-5 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <div className="flex flex-wrap gap-2.5">
              {visibleTopics.map((topic) => {
                const isActive = topic.id === selected;
                const scale = topic.heat >= 85 ? 'text-base' : topic.heat >= 65 ? 'text-sm' : 'text-xs';
                return (
                  <button
                    key={topic.id}
                    type="button"
                    onClick={() => setSelected(topic.id)}
                    className={`whitespace-nowrap rounded-full border px-4 py-2 transition-all ${scale} ${
                      isActive
                        ? 'border-primary-500 bg-primary-500 font-semibold text-background-50'
                        : 'border-background-200 bg-background-100 text-foreground-700 hover:border-primary-300 hover:text-primary-600'
                    }`}
                  >
                    <span className="mr-1 text-primary-500">#</span>
                    {topic.name}
                    {topic.heat >= 85 && <i className="ri-fire-fill ml-1.5 text-accent-600" aria-hidden="true" />}
                  </button>
                );
              })}
            </div>

            <div className="mt-6 rounded-lg border border-background-200 bg-background-100 p-5">
              <div className="flex items-center gap-2 text-sm text-foreground-600">
                <i className="ri-chat-forward-line text-primary-500" aria-hidden="true" />
                当前选中话题
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-3">
                <span className="font-heading text-lg font-bold text-foreground-950">
                  # {selectedTopic.name}
                </span>
                <span className="rounded-full bg-primary-100 px-2.5 py-0.5 text-xs font-medium text-primary-700">
                  热度 {selectedTopic.heat}
                </span>
              </div>
              <p className="mt-2 text-sm text-foreground-600">{selectedTopic.note}</p>
              <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-background-200">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-primary-400 to-accent-500 transition-all duration-500"
                  style={{ width: `${selectedTopic.heat}%` }}
                />
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-100 p-5">
            <div className="flex items-center justify-between">
              <h3 className="font-heading text-base font-bold text-foreground-950">话题热度榜</h3>
              <span className="flex items-center gap-1 text-xs text-accent-700">
                <i className="ri-line-chart-line" aria-hidden="true" />
                实时
              </span>
            </div>
            <ol className="mt-4 space-y-3.5">
              {hotRanking.map((topic, index) => (
                <li key={topic.id}>
                  <button
                    type="button"
                    onClick={() => {
                      setActiveGroup('all');
                      setSelected(topic.id);
                    }}
                    className="flex w-full items-center gap-3 text-left"
                  >
                    <span
                      className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-xs font-bold ${
                        index < 3 ? 'bg-accent-500 text-primary-950' : 'bg-background-200 text-foreground-600'
                      }`}
                    >
                      {index + 1}
                    </span>
                    <span className="flex-1">
                      <span className="block text-sm text-foreground-800">{topic.name}</span>
                      <span className="mt-1 block h-1 w-full overflow-hidden rounded-full bg-background-200">
                        <span
                          className="block h-full rounded-full bg-primary-400"
                          style={{ width: `${topic.heat}%` }}
                        />
                      </span>
                    </span>
                    <span className="text-xs text-foreground-500">{topic.heat}</span>
                  </button>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>
    </section>
  );
}